import React, { useRef } from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { buildLanguageUrl } from '../../utils/languageUrl';
import { useDialog } from '../../utils/useDialog';
import './LanguagePicker.scss';

/**
 * LanguagePicker - Locale switcher popup for MegaMenu
 *
 * Renders a button showing the current language that opens a popup
 * listing available languages. Selecting a language navigates to
 * the appropriate locale URL.
 *
 * Features:
 * - Controlled open/close state via isOpen + onToggle props
 * - Button displays current language name
 * - Popup with language list
 * - Path-based or query param locale switching
 * - Closes on outside click and Escape key
 * - Self-contained: receives all data as props
 */

export interface Language {
  /** Language code (e.g. 'en-US') */
  code: string;
  /** Display name (e.g. 'English - US') */
  name: string;
  /** BCP-47 language subtag (e.g. 'EN'). Derived from code if not provided. */
  bcp47?: string;
}

export interface LanguagePickerProps {
  /** Current selected language code */
  currentLanguage: string;
  /** Available languages for selection */
  languages: Language[];
  /** Popup heading text */
  heading?: string;
  /** Add locale to URL path (e.g. /en/page) vs query param only */
  addPathLocale?: boolean;
  /** Controlled open state — managed by parent (MegaMenu shell) */
  isOpen: boolean;
  /** Toggle callback — parent controls open/close */
  onToggle: () => void;
  /** Close callback — idempotent, always sets closed */
  onClose: () => void;
  /**
   * Current pathname captured client-side. Pass empty string for SSR / first
   * render so generated href values stay consistent across server and client
   * renders (avoids React hydration mismatch).
   */
  currentPathname?: string;
  /**
   * Current search string captured client-side. Pass empty string for SSR /
   * first render. After mount, pass `window.location.search`.
   */
  currentSearch?: string;
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
  currentLanguage,
  languages,
  heading = 'Select your preferred language',
  addPathLocale = false,
  isOpen,
  onToggle,
  onClose,
  currentPathname = '',
  currentSearch = '',
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const currentLang = languages.find((l) => l.code === currentLanguage);
  const currentLabel = currentLang?.name ?? currentLanguage;

  const close = () => {
    onClose();
    triggerRef.current?.focus();
  };

  useDialog({
    isOpen,
    containerRef: popupRef,
    onEscape: close,
    onOutsideClick: onClose,
    outsideClickIgnoreRefs: [triggerRef],
    initialFocusRef: closeBtnRef,
    focusableSelector: 'a[href], button',
  });

  return (
    <div className="poly-language-picker">
      <button
        type="button"
        className="poly-language-picker__trigger"
        ref={triggerRef}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <CustomIcon name="language" className="poly-language-picker__icon" aria-hidden="true" />
        <span className="poly-language-picker__label">{currentLabel}</span>
      </button>
      {isOpen && (
        <div className="poly-language-picker__popup" ref={popupRef} role="dialog" aria-label={heading}>
          <button
            type="button"
            className="poly-language-picker__popup-close"
            ref={closeBtnRef}
            onClick={close}
            aria-label="Close"
          >
            <CustomIcon name="close" size="1.25em" aria-hidden="true" />
          </button>
          <h3 className="poly-language-picker__heading">{heading}</h3>
          <ul className="poly-language-picker__list" role="list">
            {languages.map((lang) => {
              const isCurrent = lang.code === currentLanguage;
              const tag = lang.bcp47 || lang.code.split('-')[0].toUpperCase();
              return (
                <li key={lang.code} className="poly-language-picker__item">
                  <a
                    href={buildLanguageUrl(lang.code, currentLanguage, addPathLocale, currentPathname, currentSearch)}
                    className={`poly-language-picker__link${isCurrent ? ' poly-language-picker__link--current' : ''}`}
                    aria-current={isCurrent ? 'true' : undefined}
                  >
                    <span>{lang.name} – {tag}</span>
                    {isCurrent && <CustomIcon name="check" size="1em" className="poly-language-picker__check" aria-hidden="true" />}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
